// src/constants/laborLawMinimums.js
// Philippine Labor Law minimum rate requirements
// These values represent the legal minimums as of the version date

export const PHILIPPINE_LABOR_LAW_MINIMUMS = {
  // Version tracking for future law updates
  version: "2025.01",
  effectiveDate: "2025-01-01",
  lastUpdated: "2025-01-01",
  
  // Minimum rate multipliers required by Philippine Labor Code
  rates: {
    // Regular overtime: minimum 125% of regular hourly rate
    overtimeRate: 1.25,
    
    // Night differential: minimum 10% additional (10PM-6AM)
    nightDiffRate: 0.10,
    
    // Regular holiday: minimum 200% of daily rate
    regularHolidayRate: 2.0,
    
    // Special non-working holiday: minimum 130% of daily rate
    specialHolidayRate: 1.3,
    
    // Rest day: minimum 130% of daily rate
    restDayRate: 1.3,
    
    // Rest day overtime: minimum 169% of hourly rate
    restDayOvertimeRate: 1.69
  },

  // NOTE: special-holiday-on-rest-day is NOT in `rates` — it is not separately
  // configurable. See SPECIAL_HOLIDAY_REST_DAY_UPLIFT below.
  
  // Descriptions for each rate type
  descriptions: {
    overtimeRate: "Work performed beyond 8 hours",
    nightDiffRate: "Work performed from 10PM to 6AM",
    regularHolidayRate: "Work on regular holidays",
    specialHolidayRate: "Work on special non-working holidays",
    restDayRate: "Work on scheduled rest days",
    restDayOvertimeRate: "Overtime work on rest days"
  },
  
  // References to legal basis
  legalBasis: {
    source: "Labor Code of the Philippines",
    articles: {
      overtime: "Article 87",
      nightDiff: "Article 86",
      holiday: "Article 94",
      restDay: "Article 93"
    }
  }
};

// Special non-working day falling on a scheduled REST DAY (BL-046).
//
// DOLE publishes this as a FLAT 150% of the daily rate — Labor Code Art. 93(c)
// ("an additional compensation of at least fifty per cent (50%)") and the
// Handbook on Workers' Statutory Monetary Benefits 2024 ed. p.22 / p.2 guide
// table ("Special (non-working) day falling on rest day 1.5"). It is NOT a
// product: deriving it as specialHolidayRate × restDayRate gives 1.69, which is
// in fact DOLE's *overtime* rate for a single premium day used as a *day* rate.
//
// Expressed as an UPLIFT rather than a hardcoded 1.5 so a company paying above
// the statutory minimum keeps its margin. Apply it over the HIGHER of the two
// component rates:
//
//     max(specialHolidayRate, restDayRate) + SPECIAL_HOLIDAY_REST_DAY_UPLIFT
//
// which mirrors DOLE's own structure — a special day is +30%, a rest day is
// +30%, and the two combined is +50%, i.e. the higher single premium plus 20
// points, NOT both premiums stacked. At the statutory floor this is
// max(1.3, 1.3) + 0.2 = 1.5.
//
// Both other formulations produce a pay INVERSION, where working a holiday on a
// rest day pays LESS than an ordinary rest day:
//   - a hardcoded 1.5 inverts against specialHolidayRate (a company at 1.6 would
//     pay more for a plain special day than for one on a rest day);
//   - anchoring only to specialHolidayRate inverts against restDayRate (a company
//     at restDayRate 1.6 would pay 1.5 for the holiday vs 1.6 for a plain rest
//     day). The settings page sets a `min` on each rate but NO max, so an
//     above-statutory restDayRate is an ordinary, savable configuration.
//
// The settings page also blocks saving any rate below its statutory minimum
// (PayrollRatesPage.js validateRates + the isValid() save gate), so the result
// can never land under 1.5 through the app. Math.max additionally coerces a
// string-typed rate from a hand-edited Firestore doc, which bare addition would
// turn into NaN pay ("1.3" + 0.2 === "1.30.2").
export const SPECIAL_HOLIDAY_REST_DAY_UPLIFT = 0.2;

// Helper function to check if a rate meets minimum requirements
export const isRateCompliant = (rateType, rateValue) => {
  const minimum = PHILIPPINE_LABOR_LAW_MINIMUMS.rates[rateType];
  if (!minimum) return true; // If no minimum defined, consider compliant
  
  // For rates, we check if the value meets or exceeds the minimum
  return rateValue >= minimum;
};

// Helper function to get compliance message
export const getComplianceMessage = (rateType, rateValue) => {
  const minimum = PHILIPPINE_LABOR_LAW_MINIMUMS.rates[rateType];
  if (!minimum) return null;
  
  if (rateValue < minimum) {
    const percentageMin = rateType === 'nightDiffRate' 
      ? `${(minimum * 100).toFixed(0)}%`
      : `${(minimum * 100).toFixed(0)}%`;
    
    return `Below legal minimum of ${percentageMin}. Philippine Labor Code requires at least ${percentageMin}.`;
  }
  
  return null;
};

// Helper function to format rate for display
export const formatRateForCompliance = (rateType, rateValue) => {
  if (rateType === 'nightDiffRate') {
    return `${(rateValue * 100).toFixed(0)}%`;
  }
  return `${(rateValue * 100).toFixed(0)}%`;
};